import { useState, useCallback, useRef, useEffect } from 'react';
import './Console.css';

export interface ConsoleEntry {
  type: 'log' | 'error' | 'warn' | 'info' | 'result';
  content: string;
  timestamp: number;
}

interface ConsoleProps {
  jsCode: string;
  onRun?: () => void;
  disabled?: boolean;
}

/**
 * Formats a value for console display
 */
function formatValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'function') return '[Function]';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/**
 * Console component for displaying and running Jertscript code output
 */
export function Console({ jsCode, onRun, disabled = false }: ConsoleProps) {
  const [entries, setEntries] = useState<ConsoleEntry[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const consoleEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new entries are added
  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries]);

  // Run the transpiled JavaScript code
  const handleRun = useCallback(() => {
    if (!jsCode || jsCode.startsWith('//') || disabled) {
      return;
    }

    setIsRunning(true);
    onRun?.();

    // Clear previous entries and add run marker
    const newEntries: ConsoleEntry[] = [
      {
        type: 'info',
        content: '--- Execution started ---',
        timestamp: Date.now()
      }
    ];

    // Capture console output by temporarily replacing console methods
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalInfo = console.info;

    console.log = (...args: unknown[]) => {
      newEntries.push({
        type: 'log',
        content: args.map(formatValue).join(' '),
        timestamp: Date.now()
      });
    };

    console.error = (...args: unknown[]) => {
      newEntries.push({
        type: 'error',
        content: args.map(formatValue).join(' '),
        timestamp: Date.now()
      });
    };

    console.warn = (...args: unknown[]) => {
      newEntries.push({
        type: 'warn',
        content: args.map(formatValue).join(' '),
        timestamp: Date.now()
      });
    };

    console.info = (...args: unknown[]) => {
      newEntries.push({
        type: 'info',
        content: args.map(formatValue).join(' '),
        timestamp: Date.now()
      });
    };

    try {
      // Execute the transpiled code using Function constructor
      // This creates a new scope for the code execution
      const executeCode = new Function(jsCode);
      const result = executeCode();

      // If there's a return value, show it
      if (result !== undefined) {
        newEntries.push({
          type: 'result',
          content: `=> ${formatValue(result)}`,
          timestamp: Date.now()
        });
      }

      // If no output was captured, show a message
      if (newEntries.length === 1) {
        newEntries.push({
          type: 'info',
          content: '// Code executed successfully (no output)',
          timestamp: Date.now()
        });
      }
    } catch (err) {
      // Handle runtime errors gracefully with French-themed message
      const errorMessage = err instanceof Error ? err.message : String(err);
      newEntries.push({
        type: 'error',
        content: `Merde! Runtime Error: ${errorMessage}`,
        timestamp: Date.now()
      });
    } finally {
      // Restore original console methods
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
      console.info = originalInfo;

      setIsRunning(false);
      setEntries(newEntries);
    }
  }, [jsCode, disabled, onRun]);

  // Clear console entries
  const handleClear = useCallback(() => {
    setEntries([]);
  }, []);

  return (
    <div className="console-container">
      <div className="console-header">
        <span className="console-title">Console</span>
        <div className="console-actions">
          <button
            className="console-clear-btn"
            onClick={handleClear}
            disabled={entries.length === 0}
          >
            Clear
          </button>
          <button
            className="console-run-btn"
            onClick={handleRun}
            disabled={isRunning || disabled}
          >
            {isRunning ? 'Running...' : 'Run'}
          </button>
        </div>
      </div>

      <div className="console-output">
        {entries.length === 0 ? (
          <div className="console-placeholder">
            // Click "Run" to execute your Jertscript code
            <br />
            // Output will appear here...
          </div>
        ) : (
          entries.map((entry, index) => (
            <div key={index} className={`console-entry console-${entry.type}`}>
              <span className="console-entry-icon">
                {entry.type === 'log' && '>'}
                {entry.type === 'error' && '!'}
                {entry.type === 'warn' && '?'}
                {entry.type === 'info' && 'i'}
                {entry.type === 'result' && '='}
              </span>
              <span className="console-entry-content">
                {entry.content}
              </span>
            </div>
          ))
        )}
        <div ref={consoleEndRef} />
      </div>
    </div>
  );
}

export default Console;
